import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form } from 'semantic-ui-react'
import { Category, Field, LoginPopupsService, PopUp } from '~/api/services/loginPopups'
import { convertDate, handleErrors } from '~/utils/helpers'
import { CHANGE_FORM_ID } from '..'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { allCategoryFields, CategoryField, FieldType } from './constants'
import AuthService, { ICountry } from '~/api/services/auth'
import * as Fields from './Fields'
import { getNestedValue, renderCategoryIcon } from './util'
import TermsCheckbox from './TermsCheckbox'
import {
  CategorySection,
  CategoryTitle,
  FarmerCard,
  FarmerCardHeader,
  FarmerCompanyName,
  FarmerStatusDot,
  FarmerTableHeader,
  FooterText,
  HeaderText,
  UnifiedGrid
} from './style'
import dayjs from 'dayjs'

type FormField = Field & { value: any; value2?: any, farmerId?: string }

interface Props {
  items: PopUp[]
  handleConfirmLastData: () => void
  headerRef?: React.RefObject<HTMLHeadingElement>
  onCanSubmitChange?: (canSubmit: boolean) => void
  onActivePopupChange?: (id: string) => void
}

const ChangeDataPopup: React.FC<Props> = ({ items, handleConfirmLastData, headerRef, onCanSubmitChange, onActivePopupChange }) => {
  const { t } = useTranslation()

  const categoryTitles: Record<Category, string> = {
    [Category.UserData]: t('User data'),
    [Category.ManagerData]: t('Manager data'),
    [Category.FarmerData]: t('Farmer data')
  }

  const methods = useForm({
    mode: 'onChange'
  })
  const { handleSubmit, reset, control } = methods

  const { fields } = useFieldArray({ control, name: 'fields' })

  const [isBusy, setIsBusy] = useState(false)
  const [popupData, setPopupData] = useState(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [categoryFields, setCategoryFields] = useState<Record<Category, CategoryField[]>>({
    [Category.UserData]: [],
    [Category.ManagerData]: [],
    [Category.FarmerData]: []
  })
  const [isLoadingConfirm, setIsLoadingConfirm] = useState(false)
  const [countries, setCountries] = useState<ICountry[]>([])
  const [fieldIndexMap, setFieldIndexMap] = useState<Record<string, number>>({})
  const [formKey, setFormKey] = useState(0)

  function getFieldIndex(category: Category, slug: string, farmerId?: string) {

    if (farmerId) {
      return fields.findIndex(field => field['category'] === Category.FarmerData && field['farmerId'] === farmerId && field['slug'] === slug)
    }

    return fieldIndexMap[`${category}:${slug}`] ?? -1
  }

  const popup = useMemo(() => {
    headerRef.current.innerText = items[activeIndex]?.title || ''
    return items[activeIndex]
  },
  [activeIndex])

  useEffect(() => {
    onActivePopupChange?.(popup?.id)
  }, [popup])

  const headerText = popup.headerText
  const footerText = popup.footerText

  useEffect(() => {
    fetchCountries()
  }, [])

  async function fetchCountries() {
    try {
      const response = await AuthService.getCountriesList()
      setCountries(response.data)
    } catch (error) {
      handleErrors(error)
    }
  }

  useEffect(() => {
    if (popup?.id) {
      fetchPopupData()
    }
  }, [popup])

  async function fetchPopupData() {
    setIsBusy(true)
    try {
      const response = await LoginPopupsService.getChangeDataPopupData(popup.id)
      const data = response.data

      if(data.birthDate) {
        data.birthDate = dayjs(data.birthDate).format('DD.MM.YYYY')
      }

      const fieldsWithAction = popup.fields.filter(field => field.action !== 0)

      const farmer = data.manager?.farmers?.[0]
      const farmerSource = farmer
        ? { ...farmer, contacts: farmer.contacts?.[0] ?? {} }
        : null

      const dataSources = {
        [Category.UserData]: data,
        [Category.ManagerData]: data.manager,
        [Category.FarmerData]: farmerSource
      }

      const updatedCategoryFields = {} as Record<Category, CategoryField[]>
      const formFields: FormField[] = []
      const indexMap: Record<string, number> = {}

      for (const category of Object.values(Category)) {
        const source = dataSources[category]
        const definitions = allCategoryFields()[category].reduce<CategoryField[]>((acc, row) => acc.concat(row), [])

        updatedCategoryFields[category] = fieldsWithAction
          .filter(field => field.category === category)
          .reduce<CategoryField[]>((acc, popupField) => {
     
          const definition = definitions.find(f => f.slug === popupField.slug)

          if (!definition) {
            return acc
          }

          indexMap[`${category}:${popupField.slug}`] = formFields.length
          if (category === Category.FarmerData) {
            data?.manager?.farmers.map(farmer => {
              if (Array.isArray(definition.key)) {
                const [firstKey, secondKey] = definition.key
                formFields.push({
                  ...popupField,
                  value: (farmer != null ? getNestedValue(popupField, farmer, firstKey) : undefined) ?? '',
                  value2: (farmer != null && secondKey ? getNestedValue(popupField, farmer, secondKey) : undefined) ?? '',
                  farmerId: farmer.farmerId
                })
              } else {
                const value = farmer != null ? getNestedValue(popupField, farmer, definition.key) : undefined
                formFields.push({ ...popupField, value: value ?? '', farmerId: farmer.farmerId })
              }
            })

          } else {
            if (Array.isArray(definition.key)) {
              const [firstKey, secondKey] = definition.key
              formFields.push({
                ...popupField,
                value: (source != null ? getNestedValue(popupField, source, firstKey) : undefined) ?? '',
                value2: (source != null && secondKey ? getNestedValue(popupField, source, secondKey) : undefined) ?? '',
                farmerId: farmer.farmerId
              })
            } else {
              const value = source != null ? getNestedValue(popupField, source, definition.key) : undefined
              formFields.push({ ...popupField, value: value ?? '', farmerId: farmer.farmerId })
            }
          }

          acc.push({ ...definition, action: popupField.action })
          return acc
        }, [])
      }
      reset({ fields: formFields })
      setFieldIndexMap(indexMap)
      setCategoryFields(updatedCategoryFields)
      setPopupData(data)
      setFormKey(prev => prev + 1)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderEditableField(field: CategoryField, category: Category, farmerId?: string, disabled?: boolean) {
    const index = getFieldIndex(category, field.slug, farmerId)

    if (index < 0) return null

    const name = `fields[${index}]`
    const commonProps = { key: field.slug, field, name, disabled }

    switch (field.type) {
      case FieldType.Select:
        return <Fields.SelectField {...commonProps} />
      case FieldType.Date:
        return <Fields.DateField {...commonProps} />
      case FieldType.BIRTH_DATE:
        return <Fields.BirthDateField {...commonProps} />
      case FieldType.MOBILE:
        return <Fields.PhoneNumberField {...commonProps} countries={countries} icon='mobile alternate' />
      case FieldType.PHONE:
        return <Fields.PhoneNumberField {...commonProps} countries={countries} icon='phone' />
      case FieldType.STREET:
        return <Fields.StreetField {...commonProps} />
      case FieldType.CITY:
        return <Fields.CityField {...commonProps} />
      case FieldType.COUNTRY:
        return <Fields.CountryField {...commonProps} countries={countries} />
      case FieldType.CANTON:
        return <Fields.CantonField {...commonProps} countries={countries} />
      case FieldType.IBAN:
        return <Fields.IbanField {...commonProps} farmerId={farmerId} />
      case FieldType.CONTACT_FIRST_NAME:
        return <Fields.ContactFirstName {...commonProps} />
      case FieldType.CONTACT_LAST_NAME:
        return <Fields.ContactLastName {...commonProps} />
      case FieldType.BANK_ACCOUNT_HOLDER:
        return <Fields.BankAccountHolderField {...commonProps} />
      default:
        return <Fields.InputField {...commonProps} countries={countries}/>
    }
  }

  function renderFieldCell(field: CategoryField, category: Category, farmerId?: string) {
    const index = getFieldIndex(category, field.slug, farmerId)
    if (index < 0) return <Form.Field key={field.slug} />

    const runtime = categoryFields[category].find(f => f.slug === field.slug)
    const action = runtime?.action ?? field.action

    const disabled = action !== 2 && action !== 3

    return <React.Fragment key={field.slug}>{renderEditableField(field, category, farmerId, disabled)}</React.Fragment>
  }

  function renderFieldGrid(category: Category, farmerId?: string) {
    return allCategoryFields()[category].map((row, i) => (
      <Form.Group key={i} widths='equal' style={{ alignItems: 'baseline' }}>
        {row.map(field => renderFieldCell(field, category, farmerId))}
      </Form.Group>
    ))
  }

  function renderGridSection(category: Category) {
    if (!categoryFields[category]?.length) return null

    return (
      <CategorySection key={category}>
        <CategoryTitle>
          {renderCategoryIcon(category)}
          {categoryTitles[category]}
        </CategoryTitle>
        <UnifiedGrid>
          {renderFieldGrid(category)}
        </UnifiedGrid>
      </CategorySection>
    )
  }

  function renderFarmerDataSection(category: Category) {
    if (!categoryFields[category]?.length) return null

    const farmers = popupData?.manager?.farmers ?? []

    return (
      <CategorySection key={category}>
        <CategoryTitle className='farms'>
          {renderCategoryIcon(category)}
          {categoryTitles[category]}
        </CategoryTitle>

        <FarmerTableHeader>
        </FarmerTableHeader>

        <div>
          {farmers.map(farmer => (
            <FarmerCard key={farmer.id}>
              <FarmerCardHeader>
                <FarmerStatusDot />
                <FarmerCompanyName>{farmer?.companyName}</FarmerCompanyName>
              </FarmerCardHeader>

              <UnifiedGrid>
                {renderFieldGrid(category, farmer?.farmerId)}
              </UnifiedGrid>
            </FarmerCard>
          ))}
        </div>
      </CategorySection>
    )
  }

  async function onSubmit(data) {
    if (activeIndex + 1 > items.length) {
      return handleConfirmLastData();
    }

    try {
      setIsLoadingConfirm(true);

      const dirtyIndexes = Object.keys(methods.formState.dirtyFields?.fields || {});

      const extractedDirtyFields = dirtyIndexes.map(
        index => {
          if(data.fields[Number(index)].slug === 'birth_date') {
            return {
              ...data.fields[Number(index)],
              value: convertDate(data.fields[Number(index)].value)
            }
          }

          return data.fields[Number(index)]
        }
      );

      await LoginPopupsService.confirmChangeDataPopup(popup.id, {
        fields: extractedDirtyFields
      });

      if (activeIndex + 1 < items.length) {
        setActiveIndex(prev => prev + 1);
        return;
      }

      handleConfirmLastData();

    } catch (error) {
      handleErrors(error);
    } finally {
      setIsLoadingConfirm(false);
    }
  }

  return (
    <FormProvider {...methods}>
      <Form id={CHANGE_FORM_ID} onSubmit={handleSubmit(onSubmit)} loading={isBusy || isLoadingConfirm}>
        {headerText && <HeaderText dangerouslySetInnerHTML={{ __html: headerText }} />}

        <React.Fragment key={formKey}>
          {Object.values(Category).map(category => {
            if (category === Category.FarmerData) {
              return renderFarmerDataSection(category)
            }
            return renderGridSection(category)
          })}
        </React.Fragment>

        {footerText && <FooterText dangerouslySetInnerHTML={{ __html: footerText }} />}

        <TermsCheckbox onCanSubmitChange={onCanSubmitChange} />
      </Form>
    </FormProvider>
  )
}

export default ChangeDataPopup
